import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import appRouter from "./appRouter";
import adminRouter from "./adminRouter";
import useCustomLogin from "../hooks/useCustomLogin";

const Loading = () => (
  <div className="flex justify-center items-center h-screen w-full">
    <div className="animate-spin border-t-2 border-b-2 border-blue-500 rounded-full w-8 h-8"></div>
    <span className="ml-2">Loading...</span>
  </div>
);

const NotFound = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate("/", { replace: true });
  }, [navigate]);
};

const Login = lazy(() => import("../pages/LoginPage"));
const Guide = lazy(() => import("../pages/GuidePage"));
const AppIndex = lazy(() => import("../pages/app/AppIndex"));
const AdminIndex = lazy(() => import("../pages/admin/AdminIndex"));
const MemberBrowseStandalone = lazy(() =>
  import("../pages/member/MemberBrowseStandalone")
);
const MemberGameApp = lazy(() => import("../pages/member/MemberGameApp"));

const getDashboardPath = (roleNames) => {
  if (roleNames.includes("SUPER") || roleNames.includes("ADMIN")) {
    return "/admin/";
  }
  return "/app/";
};

const RequireAuth = ({ children, isAdminRoute }) => {
  const { isLogin, loginInfo, refreshToken, doLogout } = useCustomLogin();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isLogin) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          doLogout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [isLogin, refreshToken, doLogout]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <div className="animate-spin border-t-2 border-b-2 border-blue-500 rounded-full w-8 h-8"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  if (!isLogin || !loginInfo) {
    if (window.location.pathname.startsWith("/app")) {
      const currentPath = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      window.location.href = `/login?returnUri=${currentPath}`;
    } else {
      window.location.href = "/login";
    }
    return null;
  }

  if (isAdminRoute) {
    const allowedRoles = ["SUPER", "ADMIN", "MANAGER"];
    const userRoles = loginInfo.roleNames;

    const hasAccess = userRoles.some((role) => allowedRoles.includes(role));

    if (!hasAccess) {
      const dashboardPath = getDashboardPath(userRoles);
      return <Navigate to={dashboardPath} state={{ from: location }} replace />;
    }
  }

  const dashboardPath = getDashboardPath(loginInfo.roleNames);
  if (location.pathname === "/") {
    return <Navigate to={dashboardPath} state={{ from: location }} replace />;
  }

  return children;
};

const InitLoginPage = ({ children }) => {
  const { initLogin } = useCustomLogin();
  useEffect(() => {
    initLogin();
  }, []);
  return children;
};

const root = createBrowserRouter(
  [
    {
      path: "login",
      element: (
        <InitLoginPage>
          <Suspense fallback={<Loading />}>
            <Login />
          </Suspense>
        </InitLoginPage>
      ),
    },
    {
      path: "/",
      element: (
        <RequireAuth>
          <Suspense fallback={<Loading />}>
            <Navigate to="/app/performance/dashboard" replace />
          </Suspense>
        </RequireAuth>
      ),
    },
    {
      path: "app",
      element: (
        <RequireAuth isAdminRoute={false}>
          <Suspense fallback={<Loading />}>
            <AppIndex />
          </Suspense>
        </RequireAuth>
      ),
      children: appRouter(),
    },
    {
      path: "admin",
      element: (
        <RequireAuth isAdminRoute={true}>
          <Suspense fallback={<Loading />}>
            <AdminIndex />
          </Suspense>
        </RequireAuth>
      ),
      children: adminRouter(),
    },
    {
      path: "guide",
      element: <Guide />,
    },
    // 새로운 독립 페이지들 추가
    {
      path: "member-browse",
      element: (
        <RequireAuth>
          <Suspense fallback={<Loading />}>
            <MemberBrowseStandalone />
          </Suspense>
        </RequireAuth>
      ),
    },
    {
      path: "member-game",
      element: (
        <RequireAuth>
          <Suspense fallback={<Loading />}>
            <MemberGameApp />
          </Suspense>
        </RequireAuth>
      ),
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

export default root;
